/**
 * **컨텐츠 레퍼런스·허브 BFF 질문의 mockup 갈래** — `actions/meta-ads/meta-ads-mock` 과 같은 모양이다.
 *
 * ```
 *   actions/search-all-similar-posts.ts    비슷한 게시물       ┐
 *   actions/search-all-video-url.ts        영상 재생 주소      │
 *   actions/search-all-image-search.ts     이미지로 검색       ├ 머리에서 resolveMock*() 한 줄
 *   actions/influencer-profile-lookup.ts   계정 얼굴 실시간 조회 ┘
 *     │  잡히면 픽스처, 아니면
 *     ▼
 *   /api/search-all/** · /api/influencer/profiles   평소 그대로
 * ```
 *
 * 모두 허브 셋(브랜드·크리에이터·컨텐츠)이 함께 쓰는 BFF 라 창구와 무관하게 여기 한 곳에 모은다.
 * ⚠️ 판정은 핸들러가 한다 — 게시물 id 가 실리는 질문은 `MOCKUP_` 접두로, 이미지(주소·파일)·계정 이름만
 * 실리는 질문은 「등록됐는가」로 가른다(등록은 mockup 라우트의 layout 이 마운트돼 있는 동안뿐이다).
 * ⚠️ 핸들러가 `null` 을 주면 「내 것이 아니다」라는 뜻이고 호출은 평소대로 서버로 간다.
 */
import type { SimilarPostItem } from 'src/types/search-all/similar-post';
import type { ImageSearchResponse } from 'src/types/search-all/image-search';
import type { VideoUrlResult, VideoUrlRequestItem } from 'src/types/search-all/video-url';
import type { ProfileLookupItem, ProfileLookupResult } from 'src/types/influencer/profile-lookup';

export type SearchAllMockHandlers = {
  /** 이미지가 비슷한 게시물 — `postId` 또는 `imageUrl` 하나가 온다 */
  similarPosts?: (query: { postId?: string; imageUrl?: string; limit: number }) => SimilarPostItem[] | null;
  /** 영상 재생 주소 — 빈 목록이면 화면은 썸네일로 남는다 */
  videoUrls?: (items: readonly VideoUrlRequestItem[]) => VideoUrlResult[] | null;
  /** 계정 얼굴·팔로워 실시간 조회 */
  profileLookups?: (items: readonly ProfileLookupItem[]) => ProfileLookupResult[] | null;
  /** 이미지로 검색 — 잡히면 이미지가 브라우저 밖으로 나가지 않는다 */
  imageSearch?: (image: Blob) => ImageSearchResponse | null;
};

/** mockup 라우트만 부른다. `null` 이면 해제다 */
export function registerSearchAllMock(next: SearchAllMockHandlers | null): void {
  handlers = next;
}

export function resolveMockSimilarPosts(query: {
  postId?: string;
  imageUrl?: string;
  limit: number;
}): SimilarPostItem[] | null {
  return handlers?.similarPosts?.(query) ?? null;
}

export function resolveMockVideoUrls(items: readonly VideoUrlRequestItem[]): VideoUrlResult[] | null {
  return handlers?.videoUrls?.(items) ?? null;
}

export function resolveMockProfileLookups(items: readonly ProfileLookupItem[]): ProfileLookupResult[] | null {
  return handlers?.profileLookups?.(items) ?? null;
}

export function resolveMockImageSearch(image: Blob): ImageSearchResponse | null {
  return handlers?.imageSearch?.(image) ?? null;
}

let handlers: SearchAllMockHandlers | null = null;
